import * as React from 'react'
import {FC} from 'react'
import {observer} from 'mobx-react'
import {PaymentMethod} from '@raisenow/epik'
import {useStores} from '../../../../lib/MobXProvider'
import {WidgetApi} from '../../../../lib/WidgetApi'
import {
  getPaymentMethodTitle,
  markFieldAsNotDirty,
} from '../../../../lib/helpers'
import {WidgetEventName} from '../../../../lib/WidgetEventer'
import {FormPart} from '../../../wrappers/FormPart'
import {RippleWrapper} from '../../../wrappers/RippleWrapper'
import {PaymentMethodForm} from '../../common/elements/PaymentMethodForm'
import {PaymentMethodDetailsHeaderInfo} from '../../common/elements/PaymentMethodDetailsHeaderInfo'
import {IconAngleLeft} from '../../../ui/Icons'

///////////////////////////////////////////////////////////////////////////////

type PaymentPaymentMethodDetailsProps = {
  topElementId: string
  paymentMethods: PaymentMethod[]
}

///////////////////////////////////////////////////////////////////////////////

export const PaymentPaymentMethodDetails: FC<PaymentPaymentMethodDetailsProps> = observer(
  (props) => {
    const {api} = useStores<{api: WidgetApi}>()
    const {topElementId, paymentMethods} = props
    const form = api.paymentForm
    const paymentMethod = form.getPaymentMethod()
    const paymentMethodTitle = getPaymentMethodTitle(
      paymentMethod,
      form,
      api.trans.bind(api),
    )
    const {uiTransitionTimeout: timeout} = api.computedConfig

    const cancelPaymentMethodSelection = () => {
      setTimeout(async () => {
        form.setLastSelectedPaymentMethod(paymentMethod)
        form.setPaymentMethod(null)
        await api.events[WidgetEventName.PAYMENT_METHOD_CHANGED].publish({api})
        markFieldAsNotDirty(form.dirtyFields, 'payment_method')
        await form.validate(true)
        await api.ui.scrollTo('#' + topElementId)
        api.ui.focusWidgetElementBySelector(
          '.payment-methods .payment-method[tabindex="0"]',
        )
      }, timeout)
    }
    const submit = () => form.validateAndSend()

    const isSinglePaymentMethodAllowed = paymentMethods.length === 1

    return (
      <div className="payment-payment-method-details">
        <div className="main">
          <section className="payment-method-details">
            <div className="header">
              <div className="title">
                <h2 className="text">{paymentMethodTitle}</h2>
              </div>
              <PaymentMethodDetailsHeaderInfo form={form} />
            </div>

            <FormPart className="main" onSubmit={submit}>
              <fieldset
                id={api.ui.generateUniqueElementId('payment-method-form')}
              >
                <legend>
                  {api.trans(
                    'blocks.payment_payment_methods.payment_method_details.legend',
                    {payment_method: paymentMethodTitle},
                  )}
                </legend>

                <PaymentMethodForm form={form} />
              </fieldset>
            </FormPart>
          </section>
        </div>

        {!isSinglePaymentMethodAllowed && (
          <RippleWrapper>
            <button
              type="button"
              className="footer"
              onClick={cancelPaymentMethodSelection}
            >
              <span className="icon">
                <IconAngleLeft />
              </span>
              <span className="text">
                {api.trans(
                  'blocks.payment_payment_methods.payment_method_details.cancel_selection',
                )}
              </span>
            </button>
          </RippleWrapper>
        )}
      </div>
    )
  },
)
